import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import {
  RECOVERY_CODE_LENGTH,
  RECOVERY_CODE_RESEND_COOLDOWN_MS,
  RECOVERY_CODE_TTL_MS,
} from '@shared/devices/device-recovery-policy'
import { requestDeviceEmailCode, verifyDeviceEmailCode } from '../../../services/appsync-client'
import { formatUserFacingError } from '../../../utils/format-error'
import { ACTIVE_APPROVER_WINDOW_DAYS } from '../utils/device-approval-status'

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
const CODE_TTL_MINUTES = Math.round(RECOVERY_CODE_TTL_MS / 60_000)

type Props = {
  open: boolean
  /** "browser" on the web, "device" in the native apps. */
  deviceNoun: 'browser' | 'device'
  onClose: () => void
  /** This install is now trusted (the caller refreshes and confirms). */
  onVerified: () => void
}

/**
 * Trust THIS install with a code emailed to the account address, when none of
 * the user's trusted devices has been used for 14 days (server-enforced).
 */
export function VerifyDeviceEmailDialog({ open, deviceNoun, onClose, onVerified }: Props) {
  const titleId = useId()
  const descId = useId()
  const codeId = useId()
  const errorId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const primaryRef = useRef<HTMLButtonElement>(null)
  const codeRef = useRef<HTMLInputElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const mountedRef = useRef(true)
  const [phase, setPhase] = useState<'intro' | 'code'>('intro')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState<'send' | 'verify' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [cooldownUntil, setCooldownUntil] = useState(0)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Reset on open; remember what to return focus to on close.
  useEffect(() => {
    if (!open) return
    returnFocusRef.current = document.activeElement as HTMLElement | null
    setPhase('intro')
    setCode('')
    setError(null)
    setStatus(null)
    setBusy(null)
    return () => {
      returnFocusRef.current?.focus?.()
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    if (phase === 'code') codeRef.current?.focus()
    else if (primaryRef.current && !primaryRef.current.disabled) primaryRef.current.focus()
    else dialogRef.current?.focus()
  }, [open, phase])

  useEffect(() => {
    if (!open || cooldownUntil <= Date.now()) return
    const id = window.setInterval(() => {
      const t = Date.now()
      setNow(t)
      if (t >= cooldownUntil) window.clearInterval(id)
    }, 1000)
    return () => window.clearInterval(id)
  }, [open, cooldownUntil])

  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - now) / 1000))

  /** A focused button that is about to be disabled would drop focus to <body>. */
  const keepFocusInDialog = useCallback(() => {
    if (document.activeElement instanceof HTMLButtonElement) dialogRef.current?.focus()
  }, [])

  const sendCode = useCallback(async () => {
    keepFocusInDialog()
    setBusy('send')
    setError(null)
    setStatus(null)
    try {
      await requestDeviceEmailCode()
      if (!mountedRef.current) return
      setCooldownUntil(Date.now() + RECOVERY_CODE_RESEND_COOLDOWN_MS)
      setNow(Date.now())
      setPhase('code')
      setCode('')
      setStatus(`We emailed a ${RECOVERY_CODE_LENGTH}-digit code to the address you sign in with.`)
      // On a resend the input is already mounted (the first send focuses it via the phase effect).
      codeRef.current?.focus()
    } catch (err) {
      if (!mountedRef.current) return
      const message = formatUserFacingError(err, 'Could not send the code. Try again.')
      if (/just sent/i.test(message)) {
        setCooldownUntil(Date.now() + RECOVERY_CODE_RESEND_COOLDOWN_MS)
        setNow(Date.now())
      }
      setError(message)
    } finally {
      if (mountedRef.current) setBusy(null)
    }
  }, [keepFocusInDialog])

  const submitCode = useCallback(async () => {
    if (code.length !== RECOVERY_CODE_LENGTH) {
      setError(`Enter the ${RECOVERY_CODE_LENGTH}-digit code from the email.`)
      codeRef.current?.focus()
      return
    }
    keepFocusInDialog()
    setBusy('verify')
    setError(null)
    setStatus(null)
    try {
      const device = await verifyDeviceEmailCode(code)
      if (!mountedRef.current) return
      if (device.status !== 'trusted') throw new Error('Could not verify this device. Try again.')
      onVerified()
    } catch (err) {
      if (!mountedRef.current) return
      setError(formatUserFacingError(err, 'Could not verify the code. Try again.'))
      codeRef.current?.focus()
    } finally {
      if (mountedRef.current) setBusy(null)
    }
  }, [code, keepFocusInDialog, onVerified])

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      onClose()
      return
    }
    if (e.key !== 'Tab' || !dialogRef.current) return
    const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
    if (items.length === 0) return
    const first = items[0]
    const last = items[items.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-slate-900/50 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl outline-none sm:p-6"
      >
        <h2 id={titleId} className="text-lg font-semibold text-slate-900">
          Verify this {deviceNoun} by email
        </h2>
        <p id={descId} className="mt-2 text-sm leading-relaxed text-slate-600">
          {phase === 'intro'
            ? `None of your trusted devices has been used in the last ${ACTIVE_APPROVER_WINDOW_DAYS} days. We'll email a ${RECOVERY_CODE_LENGTH}-digit code to the address you sign in with. Entering it trusts this ${deviceNoun} and removes those unused devices.`
            : `Enter the code from the email. It expires in ${CODE_TTL_MINUTES} minutes.`}
        </p>

        {status && (
          <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
            {status}
          </p>
        )}
        {error && (
          <p id={errorId} className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        {phase === 'intro' ? (
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 outline-none hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              Cancel
            </button>
            <button
              ref={primaryRef}
              type="button"
              disabled={busy !== null || cooldownLeft > 0}
              onClick={() => void sendCode()}
              className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white outline-none hover:bg-brand-700 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {busy === 'send'
                ? 'Sending…'
                : cooldownLeft > 0
                  ? `Try again in ${cooldownLeft}s`
                  : error
                    ? 'Try again'
                    : 'Email me a code'}
            </button>
          </div>
        ) : (
          <form
            className="mt-4"
            onSubmit={(e) => {
              e.preventDefault()
              void submitCode()
            }}
          >
            <label htmlFor={codeId} className="block text-sm font-medium text-slate-700">
              Verification code
            </label>
            <input
              id={codeId}
              ref={codeRef}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, RECOVERY_CODE_LENGTH))}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={RECOVERY_CODE_LENGTH}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-lg tracking-[0.3em] outline-none ring-brand-500 focus:ring-2"
            />
            <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                disabled={busy !== null || cooldownLeft > 0}
                onClick={() => void sendCode()}
                className="rounded-xl px-2 py-2 text-sm font-semibold text-brand-700 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-brand-500 disabled:text-slate-400 disabled:no-underline"
              >
                {busy === 'send'
                  ? 'Sending…'
                  : cooldownLeft > 0
                    ? `Resend code in ${cooldownLeft}s`
                    : 'Resend code'}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 outline-none hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy !== null || code.length !== RECOVERY_CODE_LENGTH}
                  className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white outline-none hover:bg-brand-700 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50"
                >
                  {busy === 'verify' ? 'Verifying…' : 'Verify'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
